import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import moment from 'moment';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

import { dateConfig, debounceConfig } from '@app/core/_config/';
import { AuthService } from '@app/core/auth';
import { FactoryService } from '@app/core/factories';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { ExportSkuVaultService } from '@app/shared/services/export-skuvault.service';
import { UIStateService } from '@app/shared/services/ui-state.service';
import { trimWala } from '@app/shared/utils/string.utils';

@Component({
  selector: 'receiving',
  templateUrl: './receiving.component.html',
  styleUrls: ['./receiving.component.scss'],
})
export class ReceivingComponent implements OnInit, OnDestroy {
  #addReceiving$ = new Subject<void>();
  addReceiving$ = this.#addReceiving$.asObservable();

  initialize :boolean = false;
  length = 0;
  pageSize = 20;
  pageSizeOptions :number[] = [5, 10, 25, 100];
  minDate :string = dateConfig.limit.min;
  maxDate :string = dateConfig.limit.max;
  debounceTime :number = debounceConfig.time;

  searchKey :string = '';
  pageTitle :string = 'Purchase Orders';

  checked :any = [];
  checkedAll :boolean = false;
  checkedCount :number = 0;

  ordersRecord :any = { data: [] };
  logistics :any[] = [];

  filter :any = {};
  params :any = {};

  constructor(
    public authService :AuthService,
    private uiStateSvc :UIStateService,
    public poService :PoService,
    public factorySvc :FactoryService,
    public exportSkuVaultSvc :ExportSkuVaultService,
    public settingsSvc :SettingsService,
  ) {}

  private onDestroy$ = new Subject();

  ngOnInit() {
    this.setFiltersOnInit();
    this.getRecords();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  handleCreateClick(e :Event) {
    e.preventDefault();
    this.#addReceiving$.next();
  }

  setOrder(order :string) {
    if (this.filter.order && this.filter.order == order) {
      this.filter.order = '-' + order;
      this.filter.sort = 'desc';
    } else {
      this.filter.order = order;
      this.filter.sort = 'asc';
    }
    this.getRecords(false, 1);
  }

  getRecords(event ?:any, page ?:number, paramsOnly ?:boolean) {
    if (this.initialize) {
      return;
    }

    if (page) {
      this.params.page = page;
    }

    this.params = Object.assign(this.params, this.filter);

    if (!this.filter.sku || this.filter.sku.trim().length == 0) {
      delete this.params.sku;
    }
    if (!this.filter.incoterm || this.filter.incoterm.trim().incoterm == 0) {
      delete this.params.incoterm;
    }
    if (!this.filter.po_number || this.filter.po_number.trim().length == 0) {
      delete this.params.po_number;
    } else {
      this.params.po_number = this.poService.getPoNumberFromPOString(
        this.filter.po_number,
      );
    }
    if (this.filter.product_type_id == 0) {
      delete this.params.product_type_id;
    }
    if (this.params.order) {
      this.params.order = trimWala(this.params.order, '-');
    }
    if (this.params.status == 0) {
      delete this.params.status;
    }
    if (!this.params.factory_id) {
      delete this.params.factory_id;
    }
    if (this.params.payment_status == 0) {
      delete this.params.payment_status;
    }
    if (this.params.receipt_status == 0) {
      delete this.params.receipt_status;
    }

    if (event) {
      this.params.page = event.pageIndex + 1;
      this.params.per_page = event.pageSize;
    }
    this.params.receivable = 1;
    if (paramsOnly) {
      return this.params;
    }

    this.uiStateSvc.setState('reportsReceivables', this.params);
    this.poService.get(this.params);
  }

  resetFilters() {
    this.filter = {};
    this.params = {
      per_page: 20,
      page: 1,
    };

    this.getRecords(false, 1);
  }

  exportPuchaseOrdersToCsv(export_type ?:any) {
    Swal.fire('Creating CSV, please wait...');
    Swal.showLoading();

    const params = Object.assign({}, this.getRecords(false, 0, true));
    delete params.page;
    delete params.per_page;
    params.all = 1;

    if (export_type) {
      delete params.payment_status;
      params.receivable = 1;
    }

    const title :string = 'Purchase Orders';

    this.poService.getAsync(params).subscribe((resp) => {
      this.exportSkuVaultSvc.exportPurchaseOrderstoCsv(resp, title, export_type);
    });
  }

  exportPuchaseOrdersToPdf(export_type ?:any) {
    Swal.fire('Creating PDF, please wait...');
    Swal.showLoading();

    const params = Object.assign({}, this.getRecords(false, 0, true));
    delete params.page;
    delete params.per_page;
    params.all = 1;

    if (export_type) {
      params.receivable = 1;
    }

    const title :string = 'Purchase Orders';

    this.poService.getAsync(params).subscribe((resp) => {
      this.exportSkuVaultSvc.exportPurchaseOrderstoPDF(resp, title, export_type);
    });
  }

  validateDate(prop :string) :void {
    const min = moment(dateConfig.limit.min);
    const max = moment(dateConfig.limit.max);

    if (this.filter.hasOwnProperty(prop)) {
      const inputDate = moment(this.filter[prop]);

      if (inputDate.isBefore(min)) {
        this.filter[prop] = inputDate.set('year', min.year()).format('YYYY-MM-DD');
      }

      if (inputDate.isAfter(max)) {
        this.filter[prop] = inputDate.set('year', max.year()).format('YYYY-MM-DD');
      }

      this.getRecords(false, 1);
    }
  }

  setFiltersOnInit() :void {
    const stateView = this.uiStateSvc.getState('reportsReceivables');
    if (stateView) {
      this.filter = stateView;
    }
  }
}
